import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "../lib/api";

/**
 * Field Operations · web view of the Phase 1 daily capture chain
 * (v3 scope §3). Steps:
 *
 *   2. Gang muster · NFC wristbands       → Muster section
 *   4. TPH audit → OPH card issued        → TPH Audits + OPH Cards
 *   5. Mechanization pickup → ramp        → Mech Pickups
 *   6. SPB dispatch to mill               → SPB Dispatch
 *
 * On mobile these are Mandor/Driver/Dispatch flows; this web page is
 * the office-side mirror — demo, backfill, and monitoring.
 */

interface Worker {
  id: string;
  name: string;
  workerType: string;
}
interface Tph {
  id: string;
  code: string;
  blockId: string;
}
interface Ramp {
  id: string;
  code: string;
  name?: string | null;
}
interface Mill {
  id: string;
  code?: string;
  name: string;
}
interface Vehicle {
  id: string;
  plateNumber: string;
  vehicleType?: string;
  defaultDriverWorkerId?: string | null;
}
interface Wristband {
  id: string;
  workerId: string;
  nfcUid: string;
  status: string;
  issuedAt: string;
}
interface AuditBatch {
  id: string;
  tphId: string;
  mode: string;
  totalBunches: number;
  ripeCount: number;
  underripeCount: number;
  unripeCount: number;
  ophCode: string | null;
  printedAt: string | null;
  auditDate: string;
}
interface Pickup {
  id: string;
  driverWorkerId: string;
  tphId: string;
  rampId: string | null;
  ophBatchIds: string[];
  totalBunches: number;
  status: string;
  pickedUpAt: string;
  deliveredAt: string | null;
}
interface Spb {
  id: string;
  spbNumber: string;
  vehicleId: string;
  totalBunches: number;
  status: string;
  dispatchedAt: string | null;
  createdAt: string;
}
interface PlanLineRow {
  line: {
    id: string;
    blockId: string;
    targetHarvestDate: string | null;
    estimatedBunches: number;
    gangId: string | null;
    mandorWorkerId: string | null;
    harvesterWorkerIds: string[];
    carrierWorkerIds: string[];
    status: string;
  };
  block: { id: string; code: string };
  mandorWorker: { id: string; name: string } | null;
}
interface Gang {
  id: string;
  code: string;
  name: string;
}

function fmtTs(v: string | null | undefined) {
  if (!v) return "—";
  return v.slice(0, 16).replace("T", " ");
}

export function FieldOps() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const workersQ = useQuery({
    queryKey: ["workers"],
    queryFn: () => api.list<Worker>("/v1/workers?limit=500"),
  });
  const tphsQ = useQuery({
    queryKey: ["tphs"],
    queryFn: () => api.list<Tph>("/v1/tphs"),
  });
  const rampsQ = useQuery({
    queryKey: ["ramps"],
    queryFn: () => api.list<Ramp>("/v1/ramps"),
  });
  const millsQ = useQuery({
    queryKey: ["mills"],
    queryFn: () => api.list<Mill>("/v1/mills"),
  });
  const vehiclesQ = useQuery({
    queryKey: ["vehicles"],
    queryFn: () => api.list<Vehicle>("/v1/vehicles"),
  });
  const wristbandsQ = useQuery({
    queryKey: ["nfc-wristbands"],
    queryFn: () => api.list<Wristband>("/v1/nfc-wristbands"),
  });
  const auditsQ = useQuery({
    queryKey: ["bunch-audits"],
    queryFn: () => api.list<AuditBatch>("/v1/bunch-audits"),
  });
  const pickupsQ = useQuery({
    queryKey: ["mech-pickups"],
    queryFn: () => api.list<Pickup>("/v1/mech-pickups"),
  });
  const spbQ = useQuery({
    queryKey: ["spb"],
    queryFn: () => api.list<Spb>("/v1/spb"),
  });
  const gangsQ = useQuery({
    queryKey: ["gangs"],
    queryFn: () => api.list<Gang>("/v1/gangs"),
  });
  // Today's plan · the bridge from Harvest Planning. Wide horizon so
  // active lines with past/future targets stay visible.
  const planQ = useQuery({
    queryKey: ["fieldops-plan"],
    queryFn: async () => {
      const d = new Date();
      const from = new Date(d.getTime() - 30 * 86400000).toISOString().slice(0, 10);
      const to = new Date(d.getTime() + 30 * 86400000).toISOString().slice(0, 10);
      const res = await fetch(
        `${import.meta.env.VITE_API_URL ?? ""}/v1/harvest-schedule?from=${from}&to=${to}`,
      );
      if (!res.ok) throw new Error(`plan fetch ${res.status}`);
      return (await res.json()) as { scheduled: PlanLineRow[] };
    },
  });

  const workers = workersQ.data?.data ?? [];
  const workerById = useMemo(() => {
    const m = new Map<string, Worker>();
    for (const w of workers) m.set(w.id, w);
    return m;
  }, [workers]);
  const harvesters = workers.filter((w) => w.workerType === "HARVESTER");
  const mandors = workers.filter((w) => w.workerType === "MANDOR");
  const drivers = workers.filter((w) => w.workerType === "DRIVER");
  const tphs = tphsQ.data?.data ?? [];
  const tphById = useMemo(() => {
    const m = new Map<string, Tph>();
    for (const t of tphs) m.set(t.id, t);
    return m;
  }, [tphs]);
  const audits = auditsQ.data?.data ?? [];
  const issuedCards = audits.filter((a) => a.ophCode);
  const unissued = audits.filter((a) => !a.ophCode);
  const pickups = pickupsQ.data?.data ?? [];
  const spbs = spbQ.data?.data ?? [];

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["bunch-audits"] });
    qc.invalidateQueries({ queryKey: ["mech-pickups"] });
    qc.invalidateQueries({ queryKey: ["spb"] });
    qc.invalidateQueries({ queryKey: ["nfc-wristbands"] });
  };

  // ── forms state ──
  const [wbWorkerId, setWbWorkerId] = useState("");
  const [wbUid, setWbUid] = useState("");

  const [auditTphId, setAuditTphId] = useState("");
  const [auditMandorId, setAuditMandorId] = useState("");
  const [auditRipe, setAuditRipe] = useState(0);
  const [auditUnderripe, setAuditUnderripe] = useState(0);
  const [auditUnripe, setAuditUnripe] = useState(0);

  const [issueBatchId, setIssueBatchId] = useState("");
  const [issueHarvesterId, setIssueHarvesterId] = useState("");

  // Plan-driven defaults · once the plan loads, pre-select the plan's
  // mandor for the audit form and its first harvester for attribution,
  // so the office mirror follows the schedule instead of starting blank.
  const planLines = planQ.data?.scheduled ?? [];
  const activePlan = planLines.filter((r) => r.line.status !== "DONE");
  useEffect(() => {
    const first = activePlan[0];
    if (!first) return;
    if (first.line.mandorWorkerId) {
      setAuditMandorId((cur) => cur || first.line.mandorWorkerId!);
    }
    const firstHarvester = first.line.harvesterWorkerIds?.[0];
    if (firstHarvester) {
      setIssueHarvesterId((cur) => cur || firstHarvester);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planQ.data]);

  const [pickupDriverId, setPickupDriverId] = useState("");
  const [pickupTphId, setPickupTphId] = useState("");
  const [pickupOphIds, setPickupOphIds] = useState<string[]>([]);

  const [spbVehicleId, setSpbVehicleId] = useState("");
  const [spbMillId, setSpbMillId] = useState("");
  const [spbRampId, setSpbRampId] = useState("");
  const [spbOphIds, setSpbOphIds] = useState<string[]>([]);

  const wrap = (fn: () => Promise<unknown>) => async () => {
    setError(null);
    try {
      await fn();
      invalidateAll();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const issueWristband = useMutation({
    mutationFn: wrap(() =>
      api.post("/v1/nfc-wristbands", { workerId: wbWorkerId, nfcUid: wbUid }),
    ),
  });
  const createAudit = useMutation({
    mutationFn: wrap(() =>
      api.post("/v1/bunch-audits", {
        tphId: auditTphId,
        mandorWorkerId: auditMandorId || null,
        mode: "MANUAL",
        totalBunches: auditRipe + auditUnderripe + auditUnripe,
        ripeCount: auditRipe,
        underripeCount: auditUnderripe,
        unripeCount: auditUnripe,
      }),
    ),
  });
  const issueOph = useMutation({
    mutationFn: wrap(() => {
      const batch = audits.find((a) => a.id === issueBatchId);
      return api.post("/v1/oph-cards/issue", {
        batchId: issueBatchId,
        attributions: [
          {
            harvesterWorkerId: issueHarvesterId,
            bunches: batch?.totalBunches ?? 0,
          },
        ],
      });
    }),
  });
  const createPickup = useMutation({
    mutationFn: wrap(() =>
      api.post("/v1/mech-pickups", {
        driverWorkerId: pickupDriverId,
        tphId: pickupTphId,
        ophBatchIds: pickupOphIds,
      }),
    ),
  });
  const createSpb = useMutation({
    mutationFn: wrap(() => {
      // Vehicle Planning link · the chosen vehicle's default driver
      // rides along onto the SPB automatically.
      const vehicle = (vehiclesQ.data?.data ?? []).find(
        (v) => v.id === spbVehicleId,
      );
      return api.post("/v1/spb", {
        vehicleId: spbVehicleId,
        driverWorkerId: vehicle?.defaultDriverWorkerId ?? null,
        toMillId: spbMillId,
        fromRampId: spbRampId || null,
        ophBatchIds: spbOphIds,
      });
    }),
  });

  const toggle = (
    id: string,
    list: string[],
    set: (v: string[]) => void,
  ) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const sel =
    "rounded border border-border px-2 py-1 text-xs bg-surface-0";
  const btn =
    "rounded bg-text-primary px-3 py-1.5 text-xs text-white hover:opacity-90 disabled:opacity-40";
  const section = "rounded border border-border";
  const secHead =
    "flex items-center justify-between border-b border-border bg-surface-2 px-3 py-2";

  return (
    <div className="space-y-6 text-sm">
      <div>
        <h1 className="text-lg font-medium">Field Operations · Delivery to POM</h1>
        <p className="text-xs text-text-secondary">
          Phase 1 daily capture chain · muster → TPH audit → OPH card →
          mechanization pickup → ramp → SPB dispatch. This page is the
          office-side mirror of the mobile flows.
        </p>
      </div>

      {error && (
        <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
          {error}
        </div>
      )}

      {/* Step 1 · Today's plan — the bridge from Harvest Planning */}
      <section className={section}>
        <div className={secHead}>
          <div className="font-medium">
            Step 1 · Today's plan (from Harvest Planning)
          </div>
          <div className="text-xs text-text-secondary">
            {activePlan.length} active line(s) · gang &amp; crew flow from the
            schedule
          </div>
        </div>
        <table className="w-full text-xs">
          <thead className="border-b border-border bg-surface-1 text-text-secondary">
            <tr>
              <th className="p-2 text-left">Block</th>
              <th className="p-2 text-left">Target date</th>
              <th className="p-2 text-right">Est. bunches</th>
              <th className="p-2 text-left">Gang</th>
              <th className="p-2 text-left">Mandor</th>
              <th className="p-2 text-left">Harvesters</th>
              <th className="p-2 text-left">Carriers</th>
              <th className="p-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {planLines.map((r) => {
              const gang = (gangsQ.data?.data ?? []).find(
                (g) => g.id === r.line.gangId,
              );
              const names = (ids: string[] | null | undefined) =>
                (ids ?? [])
                  .map((id) => workerById.get(id)?.name ?? "?")
                  .join(", ") || "—";
              const crewMissing =
                !r.line.gangId &&
                !(r.line.harvesterWorkerIds ?? []).length &&
                r.line.status !== "DONE";
              return (
                <tr key={r.line.id} className="border-b border-border">
                  <td className="p-2 font-mono font-medium">{r.block.code}</td>
                  <td className="p-2">
                    {r.line.targetHarvestDate ?? (
                      <span className="text-text-secondary">unscheduled</span>
                    )}
                  </td>
                  <td className="p-2 text-right tabular-nums">
                    {r.line.estimatedBunches}
                  </td>
                  <td className="p-2">
                    {gang ? (
                      <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-800">
                        {gang.code} · {gang.name}
                      </span>
                    ) : (
                      <span
                        className="text-amber-700"
                        title="Assign a gang on the Harvesting page (Edit → Gang) — crew auto-fills from the gang roster"
                      >
                        no gang
                      </span>
                    )}
                  </td>
                  <td className="p-2">
                    {r.mandorWorker?.name ??
                      (r.line.mandorWorkerId
                        ? workerById.get(r.line.mandorWorkerId)?.name
                        : "—") ??
                      "—"}
                  </td>
                  <td className="p-2 text-text-secondary">
                    {names(r.line.harvesterWorkerIds)}
                  </td>
                  <td className="p-2 text-text-secondary">
                    {names(r.line.carrierWorkerIds)}
                  </td>
                  <td className="p-2">
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] ${
                        r.line.status === "DONE"
                          ? "bg-emerald-100 text-emerald-800"
                          : crewMissing
                            ? "bg-amber-100 text-amber-800"
                            : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {r.line.status === "DONE"
                        ? "DONE"
                        : crewMissing
                          ? "NEEDS CREW"
                          : r.line.status}
                    </span>
                  </td>
                </tr>
              );
            })}
            {!planLines.length && (
              <tr>
                <td colSpan={8} className="p-4 text-center text-text-secondary">
                  No scheduled blocks. Create the plan on the Harvesting page
                  first — census → schedule → assign gang.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {/* Step 2 · Muster / NFC wristbands */}
      <section className={section}>
        <div className={secHead}>
          <div className="font-medium">Step 2 · Gang muster — NFC wristbands</div>
          <div className="text-xs text-text-secondary">
            {wristbandsQ.data?.data?.length ?? 0} issued
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3 text-xs">
          <label className="flex flex-col gap-1">
            <span>Harvester</span>
            <select value={wbWorkerId} onChange={(e) => setWbWorkerId(e.target.value)} className={sel}>
              <option value="">— select —</option>
              {harvesters.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span>NFC UID</span>
            <input value={wbUid} onChange={(e) => setWbUid(e.target.value)} placeholder="04A3B2C1…" className={sel} />
          </label>
          <button onClick={() => issueWristband.mutate()} disabled={!wbWorkerId || !wbUid} className={btn}>
            Issue wristband
          </button>
        </div>
        <table className="w-full text-xs">
          <thead className="border-b border-border bg-surface-1 text-text-secondary">
            <tr>
              <th className="p-2 text-left">Harvester</th>
              <th className="p-2 text-left">NFC UID</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Issued</th>
            </tr>
          </thead>
          <tbody>
            {(wristbandsQ.data?.data ?? []).map((w) => (
              <tr key={w.id} className="border-b border-border">
                <td className="p-2">{workerById.get(w.workerId)?.name ?? "—"}</td>
                <td className="p-2 font-mono">{w.nfcUid}</td>
                <td className="p-2">{w.status}</td>
                <td className="p-2 text-text-secondary">{fmtTs(w.issuedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Step 4a · TPH audit */}
      <section className={section}>
        <div className={secHead}>
          <div className="font-medium">Step 4 · TPH audit (bunch count + grade)</div>
          <div className="text-xs text-text-secondary">
            {audits.length} audit(s) · {unissued.length} awaiting OPH issue
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3 text-xs">
          <label className="flex flex-col gap-1">
            <span>TPH</span>
            <select value={auditTphId} onChange={(e) => setAuditTphId(e.target.value)} className={sel}>
              <option value="">— select —</option>
              {tphs.map((t) => (
                <option key={t.id} value={t.id}>{t.code}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span>Mandor</span>
            <select value={auditMandorId} onChange={(e) => setAuditMandorId(e.target.value)} className={sel}>
              <option value="">— select —</option>
              {mandors.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span>Ripe</span>
            <input type="number" min={0} value={auditRipe} onChange={(e) => setAuditRipe(Number(e.target.value) || 0)} className={sel + " w-20"} />
          </label>
          <label className="flex flex-col gap-1">
            <span>Underripe</span>
            <input type="number" min={0} value={auditUnderripe} onChange={(e) => setAuditUnderripe(Number(e.target.value) || 0)} className={sel + " w-20"} />
          </label>
          <label className="flex flex-col gap-1">
            <span>Unripe</span>
            <input type="number" min={0} value={auditUnripe} onChange={(e) => setAuditUnripe(Number(e.target.value) || 0)} className={sel + " w-20"} />
          </label>
          <button
            onClick={() => createAudit.mutate()}
            disabled={!auditTphId || auditRipe + auditUnderripe + auditUnripe <= 0}
            className={btn}
          >
            Record audit
          </button>
          <span className="text-text-secondary">
            (mobile flow adds the photo + AI count · this is the manual mirror)
          </span>
        </div>
        <div className="flex flex-wrap items-end gap-2 border-b border-border bg-surface-1 p-3 text-xs">
          <span className="font-medium">Issue OPH card:</span>
          <label className="flex flex-col gap-1">
            <span>Audit batch</span>
            <select value={issueBatchId} onChange={(e) => setIssueBatchId(e.target.value)} className={sel}>
              <option value="">— unissued batches —</option>
              {unissued.map((a) => (
                <option key={a.id} value={a.id}>
                  {tphById.get(a.tphId)?.code ?? a.tphId.slice(0, 6)} · {a.totalBunches} bunches · {fmtTs(a.auditDate)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span>Attribute to harvester</span>
            <select value={issueHarvesterId} onChange={(e) => setIssueHarvesterId(e.target.value)} className={sel}>
              <option value="">— select —</option>
              {harvesters.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </label>
          <button onClick={() => issueOph.mutate()} disabled={!issueBatchId || !issueHarvesterId} className={btn}>
            Issue OPH card
          </button>
        </div>
        <table className="w-full text-xs">
          <thead className="border-b border-border bg-surface-1 text-text-secondary">
            <tr>
              <th className="p-2 text-left">OPH code</th>
              <th className="p-2 text-left">TPH</th>
              <th className="p-2 text-right">Bunches</th>
              <th className="p-2 text-right">Ripe / Under / Unripe</th>
              <th className="p-2 text-left">Mode</th>
              <th className="p-2 text-left">Printed</th>
            </tr>
          </thead>
          <tbody>
            {audits.map((a) => (
              <tr key={a.id} className="border-b border-border">
                <td className="p-2 font-mono">
                  {a.ophCode ?? <span className="text-amber-700">not issued</span>}
                </td>
                <td className="p-2 font-mono">{tphById.get(a.tphId)?.code ?? "—"}</td>
                <td className="p-2 text-right tabular-nums font-medium">{a.totalBunches}</td>
                <td className="p-2 text-right tabular-nums">
                  {a.ripeCount} / {a.underripeCount} / {a.unripeCount}
                </td>
                <td className="p-2">{a.mode}</td>
                <td className="p-2 text-text-secondary">{fmtTs(a.printedAt)}</td>
              </tr>
            ))}
            {!audits.length && (
              <tr><td colSpan={6} className="p-4 text-center text-text-secondary">No TPH audits yet. Record one above.</td></tr>
            )}
          </tbody>
        </table>
      </section>

      {/* Step 5 · Mechanization pickup */}
      <section className={section}>
        <div className={secHead}>
          <div className="font-medium">Step 5 · Mechanization pickup (TPH → ramp)</div>
          <div className="text-xs text-text-secondary">{pickups.length} pickup(s)</div>
        </div>
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3 text-xs">
          <label className="flex flex-col gap-1">
            <span>Driver</span>
            <select value={pickupDriverId} onChange={(e) => setPickupDriverId(e.target.value)} className={sel}>
              <option value="">— select —</option>
              {drivers.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span>TPH</span>
            <select value={pickupTphId} onChange={(e) => setPickupTphId(e.target.value)} className={sel}>
              <option value="">— select —</option>
              {tphs.map((t) => (
                <option key={t.id} value={t.id}>{t.code}</option>
              ))}
            </select>
          </label>
          <div className="flex flex-col gap-1">
            <span>Scan OPH cards</span>
            <div className="flex max-w-md flex-wrap gap-1">
              {issuedCards.map((a) => (
                <button
                  key={a.id}
                  onClick={() => toggle(a.id, pickupOphIds, setPickupOphIds)}
                  className={`rounded border px-2 py-0.5 font-mono ${pickupOphIds.includes(a.id) ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-border"}`}
                >
                  {a.ophCode}
                </button>
              ))}
              {!issuedCards.length && (
                <span className="text-text-secondary">no issued OPH cards yet</span>
              )}
            </div>
          </div>
          <button
            onClick={() => createPickup.mutate()}
            disabled={!pickupDriverId || !pickupTphId || !pickupOphIds.length}
            className={btn}
          >
            Start pickup
          </button>
        </div>
        <table className="w-full text-xs">
          <thead className="border-b border-border bg-surface-1 text-text-secondary">
            <tr>
              <th className="p-2 text-left">Driver</th>
              <th className="p-2 text-left">TPH</th>
              <th className="p-2 text-right">OPHs</th>
              <th className="p-2 text-right">Bunches</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Picked up</th>
              <th className="p-2 text-left">Delivered</th>
              <th className="p-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {pickups.map((p) => (
              <tr key={p.id} className="border-b border-border">
                <td className="p-2">{workerById.get(p.driverWorkerId)?.name ?? "—"}</td>
                <td className="p-2 font-mono">{tphById.get(p.tphId)?.code ?? "—"}</td>
                <td className="p-2 text-right tabular-nums">
                  {Array.isArray(p.ophBatchIds) ? p.ophBatchIds.length : 0}
                </td>
                <td className="p-2 text-right tabular-nums font-medium">{p.totalBunches}</td>
                <td className="p-2">
                  <span className={`rounded px-2 py-0.5 text-[10px] ${p.status === "DELIVERED" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                    {p.status}
                  </span>
                </td>
                <td className="p-2 text-text-secondary">{fmtTs(p.pickedUpAt)}</td>
                <td className="p-2 text-text-secondary">{fmtTs(p.deliveredAt)}</td>
                <td className="p-2 text-center">
                  {p.status !== "DELIVERED" && (
                    <button
                      onClick={wrap(() => api.post(`/v1/mech-pickups/${p.id}/deliver`, {}))}
                      className="rounded bg-emerald-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-emerald-700"
                    >
                      Deliver at ramp
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!pickups.length && (
              <tr><td colSpan={8} className="p-4 text-center text-text-secondary">No pickups yet.</td></tr>
            )}
          </tbody>
        </table>
      </section>

      {/* Step 6 · SPB dispatch */}
      <section className={section}>
        <div className={secHead}>
          <div className="font-medium">Step 6 · SPB dispatch (ramp → mill)</div>
          <div className="text-xs text-text-secondary">{spbs.length} SPB(s)</div>
        </div>
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3 text-xs">
          <label className="flex flex-col gap-1">
            <span>Vehicle</span>
            <select value={spbVehicleId} onChange={(e) => setSpbVehicleId(e.target.value)} className={sel}>
              <option value="">— select —</option>
              {(vehiclesQ.data?.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>{v.plateNumber}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span>Ramp</span>
            <select value={spbRampId} onChange={(e) => setSpbRampId(e.target.value)} className={sel}>
              <option value="">— optional —</option>
              {(rampsQ.data?.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>{r.code}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span>Mill</span>
            <select value={spbMillId} onChange={(e) => setSpbMillId(e.target.value)} className={sel}>
              <option value="">— select —</option>
              {(millsQ.data?.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </label>
          <div className="flex flex-col gap-1">
            <span>OPH cards to load</span>
            <div className="flex max-w-md flex-wrap gap-1">
              {issuedCards.map((a) => (
                <button
                  key={a.id}
                  onClick={() => toggle(a.id, spbOphIds, setSpbOphIds)}
                  className={`rounded border px-2 py-0.5 font-mono ${spbOphIds.includes(a.id) ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-border"}`}
                >
                  {a.ophCode}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => createSpb.mutate()}
            disabled={!spbVehicleId || !spbMillId || !spbOphIds.length}
            className={btn}
          >
            Build SPB
          </button>
        </div>
        <table className="w-full text-xs">
          <thead className="border-b border-border bg-surface-1 text-text-secondary">
            <tr>
              <th className="p-2 text-left">SPB No.</th>
              <th className="p-2 text-right">Bunches</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Departed</th>
              <th className="p-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {spbs.map((s) => (
              <tr key={s.id} className="border-b border-border">
                <td className="p-2 font-mono font-medium">{s.spbNumber}</td>
                <td className="p-2 text-right tabular-nums">{s.totalBunches}</td>
                <td className="p-2">
                  <span className={`rounded px-2 py-0.5 text-[10px] ${s.status === "DISPATCHED" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
                    {s.status}
                  </span>
                </td>
                <td className="p-2 text-text-secondary">{fmtTs(s.dispatchedAt)}</td>
                <td className="p-2 text-center">
                  {s.status === "DRAFT" && (
                    <button
                      onClick={wrap(() => api.post(`/v1/spb/${s.id}/depart`, {}))}
                      className="rounded bg-emerald-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-emerald-700"
                    >
                      Record departure
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!spbs.length && (
              <tr><td colSpan={5} className="p-4 text-center text-text-secondary">No SPBs yet.</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
